import { Title1, Spinner, Text, Subtitle1, Title3, Divider, Accordion, AccordionItem, AccordionHeader, AccordionPanel, Checkbox, Button } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getPlacement, getPlacementByToken, getPlacementRequest, getPlacementRequestByToken, updatePlacement, updatePlacementDocumentByToken, updatePlacementRequestReceiver, updatePlacementRequestReceiverByToken } from "../../services/placementService";
import { PlacementDoc, PlacementRequest, PlacementRequestReceiver, PlacementRequestReceiverResponse } from "../../models/placements";
import { idText } from "typescript";

export const PlacementRequestComponent = () => {
  const [searchParams] = useSearchParams();
  const [request, setRequest] = useState<PlacementRequest>();
  const [placement, setPlacement] = useState<PlacementDoc>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [responseSubmitted, setResponseSubmitted] = useState<boolean>(false);
  const [receiver, setReceiver] = useState<PlacementRequestReceiver | null>(null);
  const [placementOpen, setPlacementOpen] = useState<boolean>(false);
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const [cancellationReason, setCancellationReason] = useState<string>("");

  const token = searchParams.get("token");
  const placementID = searchParams.get("placementID");
  const tenderID = searchParams.get("tenderID");
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const requestResult = await getPlacementRequestByToken(token!);
        setRequest(requestResult);
        setReceiver(requestResult.placementRequestReceivers[0]);
        
        const placementResult = await getPlacementByToken(token!);
        setPlacement(placementResult);
      } catch (error) {
        console.error("Error fetching placement request or document:", error);
        setError("Det gick inte att ladda upphandlingsförfrågan. Kontrollera att länken är korrekt.");
      } finally {
        setIsLoading(false);
      }
    }

    const fetchDataByID = async () => {
      setIsLoading(true);
      setError(null);
      try{

        const requestResult = await getPlacementRequest(placementID!);
        setRequest(requestResult);
        const foundReceiver = requestResult.placementRequestReceivers.find(
          prr => prr.placementRequestReceiverReference?.toString() === tenderID
        );
        setReceiver(foundReceiver ?? null);
        
        const placementResult = await getPlacement(placementID!);
        setPlacement(placementResult);
      } catch (error) {
        console.error("Error fetching placement request or document by ID:", error);
        setError("Det gick inte att ladda upphandlingsförfrågan. Kontrollera att länken är korrekt.");
      } finally {
        setIsLoading(false);
      }
    }
    
    if (token) {
      fetchData();
    }
    else if(placementID){
      fetchDataByID();
    } 
    else {
      setError("Ingen giltig token hittades i URL:en.");
    }
  }, [searchParams]);

  useEffect(() => {
    if(receiver){
      if (receiver.placementRequestReceiverStatus !== 'Pending' || receiver.placementRequestReceiverStatus === undefined) {
        setResponseSubmitted(true);
      }
    }
  }, [receiver]);

  useEffect(() => {
    if(placement) {
      setPlacementOpen(new Date(placement.answerDate) > new Date());
      setIsCancelled(placement.status === "cancelled");
      setCancellationReason(placement.cancellationReason || "");
    }
  }, [placement]);



  const handleResponse = async (isAccepted: boolean | null) => {
    setIsUpdating(true);
    try {
      const response: PlacementRequestReceiverResponse = {
        isPlacementRequestAccepted: isAccepted
      };

      if(token){
        await updatePlacementRequestReceiverByToken(token, response);
      }
      else if(receiver && receiver.placementRequestReceiverID){
        await updatePlacementRequestReceiver(receiver.placementRequestReceiverID, response)
      }
      
      if(isAccepted !== null && receiver) {
        setReceiver({
          ...receiver,
          placementRequestReceiverStatus: isAccepted ? "Accepted" : "Rejected"
        });
        setResponseSubmitted(true);
      }

      if(placement){
        let updatedLogs = placement?.logs || {};
        
        let tenderChangeMessage = "";
        if(isAccepted === false) {
          tenderChangeMessage = `Verksamhet nekar förfrågan`;
        }
        else if(isAccepted === true) {
          tenderChangeMessage = `Verksamhet accepterar förfrågan`;
        }
        else{
          tenderChangeMessage = "Verksamhet återkallar svar";
        }
        
        updatedLogs.tenderChanges.push(
          {
            date: new Date().toISOString(),
            tenderChange: tenderChangeMessage,
            tender: placement?.tenderName,
            filters: placement?.activeFilters.filters
          }
        );

        setPlacement({
          ...placement,
          logs: updatedLogs
        });

        if(token){
          updatePlacementDocumentByToken(placement, token);
        }
      }

      if(isAccepted === null) {
        setResponseSubmitted(false);
      }
      
      console.log(`Response submitted: ${isAccepted ? 'Accepted' : 'Rejected'}`);
      
    } catch (error) {
      console.error("Error updating placement request response:", error);
      setError("Det gick inte att skicka ditt svar. Försök igen senare.");
    } finally {
      setIsUpdating(false);
    }
  };

  

 return (
    <>
      <div className="flex justify-center bg-[#ebebeb] min-h-screen">
        <div className="flex flex-col w-full items-center h-36 p-4 bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-md w-full max-w-[1480px] mt-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Spinner size="large" />
                <Text size={500}>Laddar upphandlingsförfrågan...</Text>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Text size={500} className="text-red-600">
                  {error}
                </Text>
              </div>
            ) : placement?.status === "cancelled" ? (
              <div className="p-4 mb-4 flex flex-col gap-3">
                <Text size={600} weight="bold" className="text-red-700 block mb-2">
                  Förfrågan med referens nummer {placement?.reference} har avbrutits
                </Text>
                <Text size={400} className="text-red-600">
                  <span className="font-semibold">Orsak: </span>
                  {cancellationReason || "Ingen orsak angiven"}
                </Text>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Title1>
                  {placement?.name || "Upphandlingsförfrågan"} - {receiver?.placementRequestReceiverFirstName}
                </Title1>
                <Title3>Placeringsförfrågan</Title3>
                <div className="mt-4 flex flex-col gap-4">
                  <Text size={400}>
                  Nedan hittar ni en förfrågan från {placement?.organizationName} gällande ett uppdrag inom ramavtalet {placement?.tenderingName}. Vänligen notera sista datum och tid för att lämna svar.
                  </Text>
                  <Text size={400}>
                  Ni kan ändra ert svar fram till svarsfristens utgång. För att komma åt förfrågan igen kan ni antingen använda länken i e-postmeddelandet eller logga in i systemet Upphandlat och navigera till menyn "Förfrågningar".
                  </Text>
                  <Text size={400}>
                  Längst ner på sidan kan ni markera om ni är intresserade eller välja att avböja förfrågan. Observera att en intresseanmälan inte är bindande. Förfrågan blir först bindande efter att en handläggare kontaktar er och ni gemensamt kommer överens om att acceptera uppdraget.
                  </Text>
                </div>
                <Divider className="my-4" />
                <div>
                  <Text size={300} weight="semibold">Förfrågan ID: </Text><Text size={300}>{placement?.reference}</Text>
                </div>
                <div>
                  <Text size={300} weight="semibold">Handläggare: </Text><Text size={300}>{placement?.user.firstName} {placement?.user.lastName}</Text>
                </div>
                <div>
                  <Text size={300} weight="semibold">E-post: </Text><Text size={300}>{placement?.user.email}</Text>
                </div>
                <div>
                  <Text size={300} weight="semibold">Delområde: </Text><Text size={300}>{placement?.tenderingPart.tenderingPart}</Text>
                </div>
                <Accordion collapsible>
                  <AccordionItem value="placementDetails">
                    <AccordionHeader>
                      <Text>Behov och bakgrund</Text>
                    </AccordionHeader>
                    <AccordionPanel className="flex flex-col gap-2">
                      <Text weight="semibold">Behov:</Text>
                      <Text>{placement?.descriptionText}</Text>
                      <Text weight="semibold">Bakgrund:</Text>
                      <Text>{placement?.backgroundText}</Text>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
                <Divider className="my-4" />
                <div>
                  <Text size={300} weight="semibold">Planerad start för uppdrag: </Text><Text size={300}>{new Date(placement?.plannedDate || '').toLocaleDateString("sv-SE")}</Text>
                </div>
                <div>
                  <Text size={300} weight="semibold">Svar senast: </Text><Text size={300}>{new Date(placement?.answerDate || '').toLocaleDateString("sv-SE")}</Text>
                </div>
                <Divider className="my-4" />
                {!placementOpen && 
                  <Text size={600}>Förfrågans svarstid har gått ut!</Text>
                }
                {!responseSubmitted && placementOpen && <Checkbox 
                  checked={agreementAccepted}
                  label="Jag intygar att jag har befogenhet från min organisation att besvara denna förfrågan."
                  onChange={(_, data) => setAgreementAccepted(data.checked === true)}
                  disabled={isUpdating}
                />}
                <div className="flex gap-4 mt-4">
                  {responseSubmitted && placementOpen &&
                  (<div className="flex flex-col gap-4 items-start">
                    <Text>
                      {receiver?.placementRequestReceiverStatus == "Accepted" ? "Markerades att verksamheten är intresserad av förfrågan" : "Förfrågan avböjdes"}
                    </Text>
                    <Button 
                      size="large" 
                      onClick={() => handleResponse(null)}
                      icon={isUpdating ? <Spinner size="tiny" /> : undefined}
                      >
                      Återkalla svar
                    </Button>
                  </div>)}
                  {!responseSubmitted && placementOpen && (
                    <>
                      <Button 
                        size="medium" 
                        appearance="primary" 
                        disabled={!agreementAccepted || isUpdating}
                        onClick={() => handleResponse(true)}
                        icon={isUpdating ? <Spinner size="tiny" /> : undefined}
                      >
                        {isUpdating ? "Skickar..." : "Intresserad"}
                      </Button>
                      <Button 
                        appearance="secondary" 
                        disabled={!agreementAccepted || isUpdating}
                        onClick={() => handleResponse(false)}
                        icon={isUpdating ? <Spinner size="tiny" /> : undefined}
                      >
                        {isUpdating ? "Skickar..." : "Avböj"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PlacementRequestComponent;